import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  ApprovalRequestDto,
  DecideApprovalRequestDto,
} from "@/features/admin/api/dto";

/**
 * Rejette une demande PENDING avec motif
 * (`POST /ApprovalRequests/{id}/reject`). Même règle des quatre yeux que
 * l'approbation (422 si approbateur = demandeur). Rôles :
 * flashpay-superadmin, flashpay-admin.
 */
export const useRejectApprovalRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "approval-requests", "reject"],
    mutationFn: ({
      approvalRequestId,
      approverKeycloakId,
      rejectionReason,
    }: {
      approvalRequestId: string;
      approverKeycloakId: string;
      rejectionReason: string;
    }) =>
      mutateData({
        url: adminEndpoints.approvalRequests.reject(approvalRequestId),
        reqBody: {
          approverKeycloakId,
          rejectionReason,
        } satisfies DecideApprovalRequestDto,
        method: "POST",
      }) as Promise<ApprovalRequestDto>,
    onSuccess: (_data, { approvalRequestId }) => {
      return invalidateAfterMutation(
        queryClient,
        adminApprovalCacheKeys(approvalRequestId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRejectApprovalRequest failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
